import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { directory, services, location } from '@cwos/api-client';
import type { FindResult } from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import {
  clientResult,
  locationResult,
  searchResults,
  serviceResult,
  workerResult,
} from './find.js';

/**
 * Global search data (doc 04 §3). Builds a tenant-scoped index from existing
 * RLS-scoped, permission-gated list endpoints, then filters client-side by the
 * query. Each source is fetched only when the principal holds its permission —
 * an unheld permission yields no data and no side channel. Sessions/schedules
 * are intentionally absent (no list endpoint exists yet — see report).
 */
export function useGlobalSearch(query: string) {
  const permissions = usePermissions();
  const { has } = permissions;
  const canDirectory = has('directory:read');
  // The SCOPE-AWARE question, not the flattened one. `GET /directory/workers`
  // refuses a program-scoped `directory:read`/`user:read` holder outright
  // (`can()` at org scope in both of its gates), so indexing the roster off the
  // flattened key list fires a request the server has already decided to
  // refuse — and caches the 403 under `['search','workers']` for the session.
  const canListWorkers = mayListWorkers(permissions);
  const canServices = has('service:read');
  const canLocations = has('location:manage');

  const workers = useQuery({
    queryKey: ['search', 'workers'],
    queryFn: () => directory.workers(),
    // The staff roster needs `user:read` as well as `directory:read`; the client
    // roster below still needs only `directory:read`. Two keys, two `enabled:`
    // flags — a palette that indexed one of them off the other's permission
    // would fire a request the server refuses and quietly cache the 403.
    enabled: canListWorkers,
    staleTime: 5 * 60_000,
  });
  const clients = useQuery({
    queryKey: ['search', 'clients'],
    queryFn: () => directory.clients(),
    enabled: canDirectory,
    staleTime: 5 * 60_000,
  });
  const types = useQuery({
    queryKey: ['search', 'service-types'],
    queryFn: () => services.types(),
    enabled: canServices,
    staleTime: 5 * 60_000,
  });
  const geofences = useQuery({
    queryKey: ['search', 'geofences'],
    queryFn: () => location.geofences(),
    enabled: canLocations,
    staleTime: 5 * 60_000,
  });

  const index = useMemo<FindResult[]>(() => {
    const out: FindResult[] = [];
    if (workers.data) for (const w of workers.data) out.push(workerResult(w));
    if (clients.data) for (const c of clients.data) out.push(clientResult(c));
    if (types.data) for (const t of types.data) out.push(serviceResult(t));
    if (geofences.data) for (const g of geofences.data) out.push(locationResult(g));
    return out;
  }, [workers.data, clients.data, types.data, geofences.data]);

  const loading = workers.isLoading || clients.isLoading || types.isLoading || geofences.isLoading;

  /**
   * Search across four sources fails in two different ways, and they were one.
   *
   * `error` was "any source errored", and the palette renders it INSTEAD of
   * results — so one failing source blanked a search that three sources had
   * answered. Flipping it produces the opposite failure: a partial index
   * reporting "no matches", which is a search saying it did not find something
   * it never looked for.
   *
   * So `error` means nothing could be searched, and `partial` means some of it
   * could not. The palette shows what it has and says the rest is missing.
   */
  const sources = [workers, clients, types, geofences];
  const failed = sources.filter((q) => q.isError).length;
  const answered = sources.filter((q) => q.data !== undefined).length;
  const error = failed > 0 && answered === 0;
  const partial = failed > 0 && answered > 0;

  /**
   * A THIRD WAY TO FIND NOTHING, AND IT WAS BEING REPORTED AS THE FIRST.
   *
   * Every source here is `enabled:` off a permission, and a DISABLED
   * react-query is neither loading nor errored — `isLoading` is false,
   * `isError` is false, `data` is undefined. So `loading`, `error` and
   * `partial` were all false, the index was empty, and the palette said "No
   * matches for X in the areas you can see": a search reporting that it did not
   * find something it never looked for.
   *
   * It reaches two different readers, and they need different sentences.
   *
   *   * THE SET HAS NOT ARRIVED, or could not be read. `has()` answers false
   *     for both, so all four sources are disabled on the FIRST PAINT OF EVERY
   *     SCREEN — the palette lives in `ShellLayout`, above the router, so this
   *     is not one screen's defect but the shell's, and a reader who opens Find
   *     before `/auth/me` settles is told their access is the reason. Unknown
   *     is not denied.
   *   * THE SET ARRIVED AND HOLDS NONE OF THE FOUR KEYS. That is an answer, and
   *     it is a REFUSAL, not a zero. "No matches" invites the reader to try
   *     another word; nothing they type will search anything.
   *
   * `searchable` counts what was actually asked rather than testing the four
   * flags again at the call site, so a fifth source cannot be added without
   * appearing in this number.
   */
  const unresolved = permissions.isLoading || permissions.isError;
  const searchable = [canListWorkers, canDirectory, canServices, canLocations].filter(
    Boolean,
  ).length;

  const results = useMemo(() => searchResults(index, query), [index, query]);

  return {
    results,
    loading,
    error,
    partial,
    unresolved,
    /** A RESOLVED set that opens none of the four sources. Never true while unresolved. */
    nothingSearchable: !unresolved && searchable === 0,
  };
}
